import { useState } from "react";
import {
  AreaChart, BarChart3, ChartNoAxesCombined, ChartScatter, Eye, EyeOff, FlaskConical, Funnel, Grid2x2, Grid3x3, Hash, LayoutDashboard, LineChart,
  PieChart, ListFilter, Percent, Plus, Sigma, SlidersHorizontal, Table2, X,
} from "lucide-react";
import {
  formatInputValue, GRAIN_LABELS, MAX_COLUMN_FILTERS, MAX_COMPARE_PERIODS, MAX_INPUTS, MAX_ROW_FILTERS, metricKey,
  type DashboardInput, type Filter, type FilterOp, type InputDef, type InputFormat, type MetricSelection, type RowFilter, type TimeGrain, type VizType,
} from "@cd/core";
import { fromBoxText, toBoxText } from "../../lib/inputs";
import { GRADIENT_SCHEMES, VIZ_SPECS } from "@cd/viz";
import type { Catalog, CatalogDimension } from "../../lib/api";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Input, Label, Select, Switch, Tip } from "../../components/ui/primitives";
import { columnFilterFields, filterFields, type FilterFields } from "./fieldList";
import { FilterValues } from "./FilterValues";
import {
  addInput, addPercentOfTotal, columnId, pivotLimitBy, setColumnFilters, setInput, toggleHidden, usedInputs, useBuilder, type Draft,
} from "./store";

const VIZ_ICONS: Record<VizType, typeof Hash> = {
  kpi: Hash, line: LineChart, area: AreaChart, bar: BarChart3, combo: ChartNoAxesCombined, pie: PieChart,
  heatmap: Grid2x2, scatter: ChartScatter, treemap: LayoutDashboard, funnel: Funnel, table: Table2, pivot: Grid3x3,
};

const DATA_LABELS: Partial<Record<VizType, string>> = { pie: "Slice labels", heatmap: "Values in cells", scatter: "Point names" };

const OPS: { op: FilterOp; label: string; types: string[] }[] = [
  { op: "in", label: "is any of", types: ["string", "number"] },
  { op: "not_in", label: "is none of", types: ["string", "number"] },
  { op: "contains", label: "contains", types: ["string"] },
  { op: "gte", label: "≥", types: ["number"] },
  { op: "lte", label: "≤", types: ["number"] },
  { op: "eq", label: "is", types: ["boolean"] },
  { op: "not_null", label: "is not empty", types: ["string", "number", "boolean"] },
  { op: "is_null", label: "is empty", types: ["string", "number", "boolean"] },
];

const Section = ({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) => (
  <section className="border-b border-border px-4 py-3.5">
    <div className="mb-2 flex items-center justify-between">
      <Label>{title}</Label>
      {action}
    </div>
    {children}
  </section>
);

export function ConfigPane({ catalog, shape, locationIds, dashboardInputs = [] }: {
  catalog: Catalog; shape: { dimensions: { type: string }[]; metrics: number }; locationIds?: string[];
  /** The input boxes already on the dashboard, so an input that shares one's id can say whose value it gets. */
  dashboardInputs?: DashboardInput[];
}) {
  const { draft, update } = useBuilder();
  const metricLabel = (ref: string) => catalog.metrics.find((m) => m.ref === ref)?.label ?? ref;
  const columnLabel = (m: MetricSelection) => (m.kind === "metric" && !m.filters ? metricLabel(m.ref) : m.label || metricLabel(m.kind === "metric" ? m.ref : ""));
  const [editing, setEditing] = useState<number | null>(null);
  const dimOf = (ref: string) => catalog.dimensions.find((d) => d.ref === ref);
  const set = (patch: Partial<Draft>) => update((d) => ({ ...d, ...patch }));
  const setConfig = (k: string, v: unknown) => update((d) => ({ ...d, viz: { ...d.viz, config: { ...d.viz.config, [k]: v } } }));

  const limitBy = pivotLimitBy(draft);
  const sortKeys = [
    ...draft.dimensions.map((d) => ({ key: d.grain ? `${d.ref}:${d.grain}` : d.ref, label: dimOf(d.ref)?.label ?? d.ref })),
    ...draft.metrics.map((m) => ({ key: metricKey(m), label: columnLabel(m) })),
  ];

  return (
    <div className="h-full overflow-y-auto">
      <Section title="Visualization">
        <div className="grid grid-cols-4 gap-1.5">
          {VIZ_SPECS.map((v) => {
            const Icon = VIZ_ICONS[v.type];
            const reason = shape.metrics ? v.check(shape as never) : null;
            const active = draft.viz.type === v.type;
            return (
              <Tip key={v.type} content={reason ?? v.description}>
                <button
                  onClick={() => update((d) => ({ ...d, viz: { type: v.type, config: d.viz.type === v.type ? d.viz.config : {} }, vizPinned: true }))}
                  className={cn(
                    "flex flex-col items-center gap-1 rounded-lg border px-1 py-2 text-[11px] transition-colors",
                    active ? "border-accent bg-accent-soft text-foreground" : "border-border hover:bg-surface-2 text-muted",
                    reason && !active && "opacity-40",
                  )}
                >
                  <Icon className="size-4" />
                  {v.label}
                </button>
              </Tip>
            );
          })}
        </div>
        <VizOptions draft={draft} setConfig={setConfig} />
      </Section>

      <Section title="Metrics" action={<FormulaButton catalog={catalog} />}>
        {!draft.metrics.length && <p className="text-xs text-subtle">Pick metrics from the left.</p>}
        <ul className="space-y-1">
          {draft.metrics.map((m, i) => (
            <li key={i} className="rounded-lg bg-surface-2 text-sm">
              <div className="flex items-center gap-2 px-2 py-1.5">
                {m.kind === "formula" ? <FlaskConical className="size-3.5 text-info-fg" /> : m.filters ? <ListFilter className="size-3.5 text-info-fg" /> : <Sigma className="size-3.5 text-subtle" />}
                <span className={cn("min-w-0 flex-1 truncate", m.hidden && "text-subtle")}>
                  {columnLabel(m)}
                  {m.hidden && <span className="ml-1.5 rounded bg-surface px-1 text-[10px] text-subtle">Hidden</span>}
                  {m.kind === "formula" && <span className="block truncate font-mono text-[10px] text-subtle">{m.expr}</span>}
                  {m.kind === "metric" && m.filters && editing !== i && (
                    <span className="block truncate text-[11px] text-subtle">{metricLabel(m.ref)}, only where {describeFilters(m.filters, catalog)}</span>
                  )}
                </span>
                {m.kind === "metric" && (
                  <Tip content={m.filters ? "Edit this column's own filters" : "Only where… — count this metric for some rows only, as its own column (e.g. drink sales next to food sales)"}>
                    <button
                      onClick={() => {
                        if (m.filters) return setEditing(editing === i ? null : i);
                        update((d) => setColumnFilters(d, i, { label: `${metricLabel(m.ref)} (filtered)`, filters: [{ ref: "", op: "in", values: [] }] }));
                        setEditing(i);
                      }}
                      className={cn("hover:text-foreground", editing === i ? "text-foreground" : "text-subtle")}
                      aria-label="Only where…"
                    >
                      <ListFilter className="size-3.5" />
                    </button>
                  </Tip>
                )}
                {m.kind === "metric" && (() => {
                  const adds = catalog.metrics.find((x) => x.ref === m.ref)?.additivity === "additive";
                  return (
                    <Tip content={adds ? "% of total — add a column with this one's share of the total across all rows" : "This metric doesn't add up across rows, so it has no total to take a share of"}>
                      <button
                        disabled={!adds}
                        onClick={() => update((d) => addPercentOfTotal(d, i, columnLabel(m)))}
                        className="text-subtle hover:text-foreground disabled:opacity-40 disabled:hover:text-subtle"
                        aria-label="% of total"
                      >
                        <Percent className="size-3.5" />
                      </button>
                    </Tip>
                  );
                })()}
                <Tip content={m.hidden ? "Hidden: formulas, the sort and row filters still use it. Click to show it." : "Hide — keep it for formulas, the sort and row filters, but don't show it"}>
                  <button
                    onClick={() => update((d) => toggleHidden(d, i))}
                    className={cn("hover:text-foreground", m.hidden ? "text-foreground" : "text-subtle")}
                    aria-label={m.hidden ? "Show column" : "Hide column"}
                  >
                    {m.hidden ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                  </button>
                </Tip>
                <button
                  onClick={() => {
                    set({ metrics: draft.metrics.filter((_, j) => j !== i) });
                    setEditing(null);
                  }}
                  className="text-subtle hover:text-foreground"
                  aria-label="Remove"
                >
                  <X className="size-3.5" />
                </button>
              </div>
              {m.kind === "metric" && m.filters && editing === i && (
                <ColumnFilters catalog={catalog} index={i} locationIds={locationIds} onDone={() => setEditing(null)} />
              )}
            </li>
          ))}
        </ul>
        {draft.metrics.length > 0 && (
          <>
            <Select
              value={draft.eachDay ? "each_day" : draft.averagePer ?? ""}
              onChange={(e) => set({
                averagePer: e.target.value === "day" ? "day" : undefined,
                eachDay: e.target.value === "each_day" || undefined,
              })}
              className="mt-2 w-full"
              aria-label="Show metrics as"
            >
              <option value="">Show totals</option>
              <option value="day">Show average per day</option>
              <option value="each_day">Work out each day, then add up</option>
            </Select>
            {draft.averagePer && (
              <p className="mt-1.5 text-xs text-subtle">
                Each value is divided by the number of days in the range it covers. Grouped by day of week, Mondays are divided
                by the number of Mondays, so weekdays compare fairly even when the range isn't whole weeks.
              </p>
            )}
            {draft.eachDay && (
              <p className="mt-1.5 text-xs text-subtle">
                Formulas run on each business day separately, then each row adds its days up. Use it when a total() should be
                that day's: a pool split by each day's hours. Every column has to add up.
              </p>
            )}
          </>
        )}
      </Section>

      {draft.metrics.length > 0 && <InputsSection dashboardInputs={dashboardInputs} />}

      {draft.dimensions.length > 0 && (
        <Section title="Groupings">
          <ul className="space-y-1">
            {draft.dimensions.map((d, i) => {
              const def = dimOf(d.ref);
              return (
                <li key={d.ref} className="flex items-center gap-2 rounded-lg bg-surface-2 px-2 py-1 text-sm">
                  <span className="flex-1 truncate">{def?.label ?? d.ref}</span>
                  {def?.type === "time" && (
                    <Select
                      value={d.grain}
                      onChange={(e) =>
                        set({ dimensions: draft.dimensions.map((x, j) => (j === i ? { ...x, grain: e.target.value as never } : x)), sort: [] })
                      }
                      className="h-6 text-xs"
                    >
                      {(def.grains ?? []).map((g) => <option key={g} value={g}>by {GRAIN_LABELS[g as TimeGrain] ?? g}</option>)}
                    </Select>
                  )}
                  <button onClick={() => set({ dimensions: draft.dimensions.filter((_, j) => j !== i), sort: [] })} className="text-subtle hover:text-foreground" aria-label="Remove">
                    <X className="size-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      <FiltersSection catalog={catalog} locationIds={locationIds} />

      {draft.dimensions.length > 0 && draft.metrics.length > 0 && <RowsSection columnLabel={columnLabel} />}

      <Section title="Compare">
        <div className="flex gap-2">
          <Select
            value={draft.compareTo ?? ""}
            onChange={(e) => set({ compareTo: (e.target.value || undefined) as Draft["compareTo"] })}
            className="flex-1"
          >
            <option value="">No comparison</option>
            <option value="previous_period">Preceding period</option>
            <option value="previous_year">Same weekdays last year</option>
          </Select>
          {draft.compareTo && (
            <Select
              value={String(draft.comparePeriods ?? 1)}
              onChange={(e) => set({ comparePeriods: Number(e.target.value) })}
              aria-label="Number of periods to compare"
            >
              {Array.from({ length: MAX_COMPARE_PERIODS }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n === 1 ? "1 back" : `${n} back`}
                </option>
              ))}
            </Select>
          )}
        </div>
        {draft.compareTo && (
          <p className="mt-1.5 text-xs text-subtle">
            {draft.compareTo === "previous_period"
              ? `The same number of days just before the dashboard's range${(draft.comparePeriods ?? 1) > 1 ? `, and the ${(draft.comparePeriods ?? 1) - 1} before that` : ""}. Last 7 days is compared with the 7 days before that.`
              : `The same range 52 weeks earlier${(draft.comparePeriods ?? 1) > 1 ? `, for each of the last ${draft.comparePeriods} years` : ""}, so Mondays are compared with Mondays. For example, Tue Sep 15 2026 is compared with Tue Sep 16 2025.`}
          </p>
        )}
      </Section>

      {draft.dimensions.length > 0 && (
        <Section title="Sort & limit">
          <div className="flex gap-2">
            <Select
              value={draft.sort[0]?.key ?? ""}
              onChange={(e) => set({ sort: e.target.value ? [{ key: e.target.value, dir: draft.sort[0]?.dir ?? "desc" }] : [] })}
              className="min-w-0 flex-1"
            >
              <option value="">Automatic</option>
              {sortKeys.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            </Select>
            <Select
              value={draft.sort[0]?.dir ?? "desc"}
              disabled={!draft.sort[0]}
              onChange={(e) => set({ sort: draft.sort[0] ? [{ ...draft.sort[0], dir: e.target.value as "asc" | "desc" }] : [] })}
            >
              <option value="desc">High → low</option>
              <option value="asc">Low → high</option>
            </Select>
          </div>
          <Input
            type="number" min={1} placeholder={limitBy ? "Top rows by total (optional)" : "Row limit (optional)"} className="mt-2"
            value={draft.limit ?? ""}
            onChange={(e) => set({ limit: e.target.value ? Math.max(1, Number(e.target.value)) : undefined })}
          />
          {limitBy && (
            <p className="mt-1.5 text-xs text-subtle">
              Keeps the {draft.limit ?? "N"} {limitBy.dir === "asc" ? "lowest" : "highest"} rows by total {metricLabel(limitBy.by)}, with every column of each.
            </p>
          )}
        </Section>
      )}
    </div>
  );
}

function VizOptions({ draft, setConfig }: { draft: Draft; setConfig: (k: string, v: unknown) => void }) {
  const c = draft.viz.config;
  const t = draft.viz.type;
  const isAxisChart = t === "line" || t === "area" || t === "bar" || t === "combo" || t === "heatmap" || t === "scatter";
  const toggles: { key: string; label: string; def: boolean }[] = [
    ...(t === "bar" ? [{ key: "stacked", label: "Stacked", def: false }, { key: "horizontal", label: "Horizontal bars", def: false }] : []),
    ...(t === "kpi" ? [{ key: "upIsGood", label: "Increase is good", def: true }] : []),
    ...(t === "table" ? [{ key: "totals", label: "Totals row", def: true }] : []),
    ...(t === "table" && !(c.colorScale && c.colorScale !== "none") ? [{ key: "dataBars", label: "Data bars", def: true }] : []),
    ...(isAxisChart || t === "pie"
      ? [{ key: "legend", label: t === "heatmap" ? "Color key" : "Legend", def: true }, { key: "dataLabels", label: DATA_LABELS[t] ?? "Value labels", def: false }]
      : []),
    ...(t === "treemap" ? [{ key: "dataLabels", label: "Values on tiles", def: false }] : []),
  ];
  const colorScale = t === "pivot" || t === "table" || t === "heatmap"
    ? ((c.colorScale as string | undefined) ?? (t === "table" ? "none" : "blue"))
    : null;
  if (!toggles.length && !isAxisChart && !colorScale) return null;
  return (
    <div className="mt-3 space-y-2">
      {isAxisChart && (
        <div className="grid grid-cols-2 gap-2">
          {([["xAxisLabel", "X-axis title"], ["yAxisLabel", "Y-axis title"], ...(t === "combo" ? [["y2AxisLabel", "Right-axis title"]] : [])] as const).map(([key, label]) => (
            <label key={key} className="space-y-1 text-xs text-muted">
              {label}
              <Input
                placeholder={t === "scatter" || t === "combo" ? "Automatic" : "None"} className="h-7 text-xs"
                value={(c[key] as string | undefined) ?? ""}
                onChange={(e) => setConfig(key, e.target.value || undefined)}
              />
            </label>
          ))}
        </div>
      )}
      {colorScale !== null && (
        <>
          <label className="flex items-center justify-between gap-3 text-sm whitespace-nowrap">
            Color scale
            <Select value={colorScale} onChange={(e) => setConfig("colorScale", e.target.value)} className="min-w-0 flex-1 text-xs">
              {GRADIENT_SCHEMES.filter((g) => t !== "heatmap" || g.id !== "none").map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
            </Select>
          </label>
          {colorScale !== "none" && (
            <label className="flex items-center justify-between text-sm">
              {colorScale === "diverging" ? "Swap colors" : "Reverse (low values darkest)"}
              <Switch checked={c.colorReverse === true} onCheckedChange={(v) => setConfig("colorReverse", v)} />
            </label>
          )}
        </>
      )}
      {toggles.map((o) => (
        <label key={o.key} className="flex items-center justify-between text-sm">
          {o.label}
          <Switch checked={(c[o.key] as boolean | undefined) ?? o.def} onCheckedChange={(v) => setConfig(o.key, v)} />
        </label>
      ))}
    </div>
  );
}

function FormulaButton({ catalog }: { catalog: Catalog }) {
  const { update, draft } = useBuilder();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [expr, setExpr] = useState("");
  const [format, setFormat] = useState("number");
  // This widget's filtered columns can be used too, as `filtered.<id>`, and its inputs as `input.<id>`.
  const columns = draft.metrics.flatMap((m) => (m.kind === "metric" && m.filters && m.id ? [{ ref: `filtered.${m.id}`, label: m.label || m.id }] : []));
  const inputs = draft.inputs.map((i) => ({ ref: `input.${i.id}`, label: i.label || i.id }));
  const refs = [...expr.matchAll(/[a-z_][a-z0-9_]*\.[a-z_][a-z0-9_]*/g)].map((x) => x[0]);
  const unknown = refs.filter((r) => !catalog.metrics.some((m) => m.ref === r) && ![...columns, ...inputs].some((c) => c.ref === r));
  const valid = label.trim() && expr.trim() && refs.some((r) => !r.startsWith("input.")) && !unknown.length;
  const insert = (ref: string) => setExpr((x) => `${x}${x && !/[\s(]$/.test(x) ? " " : ""}${ref}`);

  const add = () => {
    const id = columnId(draft, label, "formula");
    update((d) => ({ ...d, metrics: [...d.metrics, { kind: "formula", id, label: label.trim(), expr: expr.trim(), format: format as never }] }));
    setOpen(false);
    setLabel("");
    setExpr("");
  };

  if (!open)
    return (
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        <Plus className="size-3" /> Formula
      </Button>
    );
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30" onClick={() => setOpen(false)}>
      <div className="w-[480px] rounded-2xl border border-border bg-surface p-5 shadow-pop" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold">Custom formula</h3>
        <p className="mt-1 text-xs text-muted">
          Combine certified metrics. Each metric is totalled first, then your formula runs on the totals — so
          <span className="font-mono"> sales / orders </span> is a true average check.
        </p>
        <div className="mt-4 space-y-3">
          <Input placeholder="Name, e.g. Average check" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
          <Input placeholder="order_items.net_sales / orders.order_count" value={expr} onChange={(e) => setExpr(e.target.value)} className="font-mono text-xs" />
          <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto">
            <Tip content="total(x): x added up over all the widget's rows, the same on every row. Sales ÷ total(sales) is each row's share.">
              <button
                onClick={() => setExpr((x) => `${x}${x && !/[\s(]$/.test(x) ? " " : ""}total()`)}
                className="inline-flex items-center gap-1 rounded-md bg-warn-bg px-1.5 py-0.5 font-mono text-[11px] text-warn-fg hover:text-foreground"
              >
                <Sigma className="size-3" /> total( )
              </button>
            </Tip>
            {columns.map((c) => (
              <button
                key={c.ref}
                onClick={() => insert(c.ref)}
                className="inline-flex items-center gap-1 rounded-md bg-info-bg px-1.5 py-0.5 text-[11px] text-info-fg hover:text-foreground"
              >
                <ListFilter className="size-3" /> {c.label}
              </button>
            ))}
            {inputs.map((c) => (
              <button
                key={c.ref}
                onClick={() => insert(c.ref)}
                className="inline-flex items-center gap-1 rounded-md bg-accent-soft px-1.5 py-0.5 text-[11px] text-accent hover:text-foreground"
              >
                <SlidersHorizontal className="size-3" /> {c.label}
              </button>
            ))}
            {catalog.metrics.map((m) => (
              <button
                key={m.ref}
                onClick={() => insert(m.ref)}
                className="rounded-md bg-surface-2 px-1.5 py-0.5 text-[11px] text-muted hover:text-foreground"
              >
                {m.label}
              </button>
            ))}
          </div>
          {unknown.length > 0 && <p className="text-xs text-bad">Unknown metric: {unknown.join(", ")}</p>}
          {!draft.inputs.length && (
            <p className="text-xs text-subtle">For a rate people will want to change, like a tip-out percent, add an input under Inputs and use it here.</p>
          )}
          <Select value={format} onChange={(e) => setFormat(e.target.value)} className="w-full">
            <option value="number">Number</option>
            <option value="currency">Currency</option>
            <option value="ratio">Percent from a ratio (0.25 → 25%)</option>
            <option value="percent">Percent, already × 100 (25 → 25%)</option>
            <option value="integer">Whole number</option>
          </Select>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="primary" disabled={!valid} onClick={add}>Add formula</Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Dashboard inputs: named numbers formulas use as `input.<id>`. On a dashboard each gets a box the
 * owner sets; the default here is what the widget runs with until then.
 */
function InputsSection({ dashboardInputs }: { dashboardInputs: DashboardInput[] }) {
  const { draft, update } = useBuilder();
  const used = new Set(usedInputs(draft).map((i) => i.id));
  return (
    <Section
      title="Inputs"
      action={
        <Button size="sm" variant="ghost" disabled={draft.inputs.length >= MAX_INPUTS} onClick={() => update((d) => addInput(d))}>
          <Plus className="size-3" /> Add
        </Button>
      }
    >
      {!draft.inputs.length && (
        <p className="text-xs text-subtle">A number people can change on the dashboard, like a tip-out rate, for formulas to use.</p>
      )}
      <ul className="space-y-2">
        {draft.inputs.map((input, i) => {
          const shared = dashboardInputs.find((x) => x.id === input.id);
          const differs = shared && (shared.default !== input.default || shared.format !== input.format);
          return (
            <li key={i} className="space-y-1.5 rounded-lg bg-surface-2 p-2 text-sm">
              <div className="flex items-center gap-2">
                <Input
                  value={input.label} placeholder="Name, e.g. Bartender tip-out" className="h-7 min-w-0 flex-1 text-xs" aria-label="Input name"
                  onChange={(e) => update((d) => setInput(d, i, { label: e.target.value }))}
                />
                <button
                  onClick={() => update((d) => ({ ...d, inputs: d.inputs.filter((_, j) => j !== i) }))}
                  className="text-subtle hover:text-foreground" aria-label="Remove input"
                >
                  <X className="size-3.5" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={input.format} className="h-7 flex-1 text-xs" aria-label="Input format"
                  onChange={(e) => update((d) => setInput(d, i, { format: e.target.value as InputFormat, default: 0 }))}
                >
                  <option value="percent">Percent</option>
                  <option value="currency">Currency</option>
                  <option value="number">Number</option>
                </Select>
                <DefaultBox key={`${input.id}:${input.format}`} input={input} onChange={(v) => update((d) => setInput(d, i, { default: v }))} />
              </div>
              <p className="text-[11px] text-subtle">
                In formulas: <span className="font-mono">input.{input.id}</span>
                {!used.has(input.id) && " · not used by a formula yet, so it isn't saved"}
              </p>
              {differs && (
                <p className="text-[11px] text-warn-fg">
                  This dashboard already has a “{shared.label}” box ({formatInputValue(shared.format, shared.saved)}), which this widget will use.
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

/** An input's default, typed as its box shows it (3 for 3%). */
function DefaultBox({ input, onChange }: { input: InputDef; onChange: (v: number) => void }) {
  const [text, setText] = useState(() => toBoxText(input.format, input.default));
  const [error, setError] = useState<string>();
  return (
    <div className="flex flex-col">
      <div className={cn("flex h-7 items-center rounded-md border bg-surface px-2 text-xs", error ? "border-bad" : "border-border")}>
        <span className="mr-1 text-subtle">Default</span>
        {input.format === "currency" && <span className="text-subtle">$</span>}
        <input
          value={text} inputMode="decimal" aria-label="Default value" aria-invalid={!!error}
          className="w-12 bg-transparent text-right tabular-nums outline-none"
          onChange={(e) => {
            setText(e.target.value);
            const r = fromBoxText(input.format, e.target.value);
            setError("error" in r ? r.error : undefined);
            if ("value" in r) onChange(r.value);
          }}
        />
        {input.format === "percent" && <span className="text-subtle">%</span>}
      </div>
      {error && <span className="text-[11px] text-bad">{error}</span>}
    </div>
  );
}

function FiltersSection({ catalog, locationIds }: { catalog: Catalog; locationIds?: string[] }) {
  const { draft, update } = useBuilder();
  // Only fields every selected metric can be filtered by.
  const fields = filterFields(catalog, draft);
  const { allowed } = fields;
  const setFilters = (filters: Filter[]) => update((d) => ({ ...d, filters }));

  return (
    <Section
      title="Filters"
      action={
        <Tip content={allowed.length ? undefined : "No field can filter the selected metrics — they would double-count."}>
          <span>
            <Button size="sm" variant="ghost" disabled={!allowed.length} onClick={() => setFilters([...draft.filters, { ref: "", op: "in", values: [] }])}>
              <Plus className="size-3" /> Add
            </Button>
          </span>
        </Tip>
      }
    >
      {!draft.filters.length && <p className="text-xs text-subtle">Date range and locations come from the dashboard.</p>}
      <ul className="space-y-2">
        {draft.filters.map((f, i) => (
          <FilterRow
            key={i}
            catalog={catalog}
            fields={fields}
            filter={f}
            locationIds={locationIds}
            onChange={(next) => setFilters(draft.filters.map((x, j) => (j === i ? next : x)))}
            onRemove={() => setFilters(draft.filters.filter((_, j) => j !== i))}
          />
        ))}
      </ul>
    </Section>
  );
}

const ROW_OPS: { op: RowFilter["op"]; label: string; values: number }[] = [
  { op: "not_null", label: "is not empty", values: 0 },
  { op: "is_null", label: "is empty", values: 0 },
  { op: "gt", label: ">", values: 1 },
  { op: "gte", label: "≥", values: 1 },
  { op: "lt", label: "<", values: 1 },
  { op: "lte", label: "≤", values: 1 },
  { op: "eq", label: "=", values: 1 },
  { op: "neq", label: "≠", values: 1 },
  { op: "between", label: "between", values: 2 },
];

/**
 * "Only show rows where…": conditions on the widget's own columns, checked after everything is
 * worked out. Unlike Filters, they don't change what's counted, and hidden rows still count toward total().
 */
function RowsSection({ columnLabel }: { columnLabel: (m: MetricSelection) => string }) {
  const { draft, update } = useBuilder();
  const columns = draft.metrics.map((m) => ({ key: metricKey(m), label: columnLabel(m) }));
  const setRows = (rowFilters: RowFilter[]) => update((d) => ({ ...d, rowFilters }));
  return (
    <Section
      title="Rows"
      action={
        <Button
          size="sm" variant="ghost" disabled={draft.rowFilters.length >= MAX_ROW_FILTERS}
          onClick={() => setRows([...draft.rowFilters, { key: columns[0]?.key ?? "", op: "not_null", values: [] }])}
        >
          <Plus className="size-3" /> Add
        </Button>
      }
    >
      {!draft.rowFilters.length && (
        <p className="text-xs text-subtle">Only show rows where a column meets a condition, e.g. Net sales over $1,000. Hidden rows still count in totals.</p>
      )}
      <ul className="space-y-2">
        {draft.rowFilters.map((f, i) => {
          const op = ROW_OPS.find((o) => o.op === f.op) ?? ROW_OPS[0]!;
          const patch = (p: Partial<RowFilter>) => setRows(draft.rowFilters.map((x, j) => (j === i ? { ...x, ...p } : x)));
          return (
            <li key={i} className="space-y-1.5 rounded-lg bg-surface-2 p-2">
              <div className="flex gap-1.5">
                <span className="self-center text-xs text-subtle">Only where</span>
                <Select value={f.key} onChange={(e) => patch({ key: e.target.value })} className="min-w-0 flex-1 text-xs" aria-label="Row filter column">
                  {!columns.some((c) => c.key === f.key) && <option value={f.key} disabled>Choose a column…</option>}
                  {columns.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </Select>
                <button onClick={() => setRows(draft.rowFilters.filter((_, j) => j !== i))} className="px-1 text-subtle hover:text-foreground" aria-label="Remove row filter">
                  <X className="size-3.5" />
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <Select value={f.op} onChange={(e) => patch({ op: e.target.value as RowFilter["op"], values: [] })} className="h-7 shrink-0 text-xs" aria-label="Row filter condition">
                  {ROW_OPS.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
                </Select>
                {op.values > 0 &&
                  Array.from({ length: op.values }, (_, n) => (
                    <Input
                      // Keyed by the stored value too: the box remounts whenever it changes (a filter above
                      // removed, an undo), so it never shows one number while the query uses another.
                      key={`${f.op}:${n}:${f.values[n] ?? ""}`} type="number" placeholder={n ? "and" : "Number, as shown (25 for 25%)"} className="h-7 min-w-0 text-xs"
                      defaultValue={f.values[n] === undefined ? "" : String(f.values[n])}
                      aria-label={n ? "Upper value" : "Value"}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        const next = [...f.values];
                        if (v && Number.isFinite(Number(v))) next[n] = Number(v);
                        else next.splice(n, 1);
                        patch({ values: next });
                      }}
                      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                    />
                  ))}
              </div>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

/** One filter: its field (from `fields`), condition and value. */
function FilterRow({ catalog, fields, filter: f, onChange, onRemove, locationIds }: {
  catalog: Catalog;
  fields: FilterFields;
  filter: Filter;
  onChange: (f: Filter) => void;
  onRemove: () => void;
  locationIds?: string[];
}) {
  const { allowed, byModel, labels } = fields;
  const def = catalog.dimensions.find((d) => d.ref === f.ref);
  const ops = OPS.filter((o) => o.types.includes(def?.type ?? "string"));
  const patch = (p: Partial<Filter>) => onChange({ ...f, ...p });
  const pickField = (ref: string) => {
    const d = catalog.dimensions.find((x) => x.ref === ref);
    patch({ ref, op: d?.type === "boolean" ? "eq" : "in", values: d?.type === "boolean" ? [true] : [] });
  };
  return (
    <li className="space-y-1.5 rounded-lg bg-surface-2 p-2">
      <div className="flex gap-1.5">
        <Select value={f.ref} onChange={(e) => pickField(e.target.value)} className="min-w-0 flex-1 text-xs" aria-label="Filter field">
          {!f.ref && <option value="" disabled>Choose a field…</option>}
          {def && !allowed.includes(def) && <option value={def.ref}>{labels.get(def.ref)}</option>}
          {byModel.map(([model, ds]) => (
            <optgroup key={model} label={model}>
              {ds.map((d) => <option key={d.ref} value={d.ref}>{labels.get(d.ref)}</option>)}
            </optgroup>
          ))}
        </Select>
        {def && (
          <Select value={f.op} onChange={(e) => patch({ op: e.target.value as FilterOp, values: [] })} className="text-xs" aria-label="Filter condition">
            {ops.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
          </Select>
        )}
        <button onClick={onRemove} className="px-1 text-subtle hover:text-foreground" aria-label="Remove filter">
          <X className="size-3.5" />
        </button>
      </div>
      {def && <FilterValue key={`${f.ref}:${f.op}`} catalog={catalog} field={def} filter={f} onChange={(values) => patch({ values })} locationIds={locationIds} />}
    </li>
  );
}

const describeFilters = (filters: Filter[], catalog: Catalog) =>
  filters
    .filter((f) => f.ref)
    .map((f) => {
      const field = catalog.dimensions.find((d) => d.ref === f.ref)?.label ?? f.ref;
      const op = OPS.find((o) => o.op === f.op)?.label ?? f.op;
      return f.op === "is_null" || f.op === "not_null" ? `${field} ${op}` : `${field} ${op} ${f.values.join(", ") || "…"}`;
    })
    .join(" and ") || "…";

/** A filtered column's name and its own filters, under its row in the Metrics list. */
function ColumnFilters({ catalog, index, locationIds, onDone }: { catalog: Catalog; index: number; locationIds?: string[]; onDone: () => void }) {
  const { draft, update } = useBuilder();
  const m = draft.metrics[index];
  if (m?.kind !== "metric" || !m.filters) return null;
  const fields = columnFilterFields(catalog, m.ref);
  const setFilters = (filters: Filter[]) => update((d) => setColumnFilters(d, index, { filters }));
  return (
    <div className="space-y-2 border-t border-border px-2 pt-2 pb-2">
      <Input
        aria-label="Column name"
        placeholder="Column name, e.g. Alcohol sales"
        className="h-7 text-xs"
        defaultValue={m.label}
        onBlur={(e) => update((d) => setColumnFilters(d, index, { label: e.target.value }))}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      />
      <p className="text-[11px] text-subtle">
        Counts {catalog.metrics.find((x) => x.ref === m.ref)?.label ?? m.ref} only where all of these hold. The widget's filters apply too.
        {m.id && <> In formulas: <span className="font-mono">filtered.{m.id}</span></>}
      </p>
      <ul className="space-y-1.5">
        {m.filters.map((f, i) => (
          <FilterRow
            key={i}
            catalog={catalog}
            fields={fields}
            filter={f}
            locationIds={locationIds}
            onChange={(next) => setFilters(m.filters!.map((x, j) => (j === i ? next : x)))}
            onRemove={() => {
              const rest = m.filters!.filter((_, j) => j !== i);
              setFilters(rest);
              if (!rest.length) onDone();
            }}
          />
        ))}
      </ul>
      <div className="flex justify-between">
        <Button size="sm" variant="ghost" disabled={m.filters.length >= MAX_COLUMN_FILTERS} onClick={() => setFilters([...m.filters!, { ref: "", op: "in", values: [] }])}>
          <Plus className="size-3" /> Add condition
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Done</Button>
      </div>
    </div>
  );
}

/** The value part of a filter, shaped by its condition: a pick list, free text, a number or yes/no. */
function FilterValue({ catalog, field, filter, onChange, locationIds }: {
  catalog: Catalog;
  field: CatalogDimension;
  filter: Filter;
  onChange: (values: Filter["values"]) => void;
  locationIds?: string[];
}) {
  switch (filter.op) {
    case "is_null":
    case "not_null":
      return null;
    case "in":
    case "not_in":
      return <FilterValues catalog={catalog} field={field} values={filter.values} onChange={onChange} locationIds={locationIds} />;
    case "eq":
      return (
        <Select value={String(filter.values[0] ?? true)} onChange={(e) => onChange([e.target.value === "true"])} className="h-7 w-full text-xs" aria-label="Value">
          <option value="true">Yes</option>
          <option value="false">No</option>
        </Select>
      );
    default: {
      const isNumber = field.type === "number";
      return (
        <Input
          type={isNumber ? "number" : "text"}
          placeholder={isNumber ? "Number" : "Text"}
          className="h-7 text-xs"
          defaultValue={filter.values[0] === undefined ? "" : String(filter.values[0])}
          onBlur={(e) => {
            const v = e.target.value.trim();
            onChange(!v ? [] : isNumber ? (Number.isFinite(Number(v)) ? [Number(v)] : []) : [v]);
          }}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        />
      );
    }
  }
}
